import { onMounted, onUnmounted, watch, type Ref } from 'vue'
import { useHelpDrawerStore } from '@/stores/useHelpDrawerStore'

type MaybeActive = Ref<boolean> | (() => boolean)

/**
 * Déclare la clé d'aide contextuelle d'une **modale** (ou d'un contexte qui se
 * superpose à la page). La clé est empilée sur `modalStack` quand le contexte
 * est actif, ce qui surcharge temporairement l'aide de la page, puis dépilée à
 * la fermeture (restaure l'aide de la page sous-jacente).
 *
 * Les **pages** n'ont pas besoin de ce composable : leur clé d'aide est dérivée
 * automatiquement de la route (voir `useHelpRouteSync`).
 *
 * Usages :
 *
 * 1. Modale montée en permanence, ouverte/fermée par un flag réactif :
 *    ```ts
 *    useContextualHelp('contact-create', isInternalOpen)
 *    ```
 *
 * 2. Contexte actif tout le temps que le composant est monté (cas rare) :
 *    ```ts
 *    useContextualHelp('mon-contexte')
 *    ```
 *
 * Si `key` est vide, le composable ne fait rien (utile pour `Modal.vue` qui
 * l'appelle de façon générique, avec `helpKey` parfois absent).
 *
 * @param key    Clé d'aide (= nom du fichier md sans suffixe de variante).
 * @param active (optionnel) condition d'activation réactive.
 */
export function useContextualHelp(key: string, active?: MaybeActive) {
  const store = useHelpDrawerStore()

  if (!key) return

  // Sans condition : actif tant que le composant est monté.
  if (active === undefined) {
    onMounted(() => store.pushModal(key))
    onUnmounted(() => store.popModal(key))
    return
  }

  // Avec condition : actif uniquement quand elle est vraie.
  const getter = typeof active === 'function' ? active : () => active.value
  let pushed = false

  watch(
    getter,
    (isActive) => {
      if (isActive && !pushed) {
        store.pushModal(key)
        pushed = true
      } else if (!isActive && pushed) {
        store.popModal(key)
        pushed = false
      }
    },
    { immediate: true },
  )

  onUnmounted(() => {
    if (pushed) {
      store.popModal(key)
      pushed = false
    }
  })
}
